import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Avatar,
  FormControlLabel,
  Radio,
  TextField,
  FormHelperText,
  RadioGroup,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputAdornment,
} from "@mui/material"; // Add TextField to the import statement
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import { styled } from "@mui/system";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom"; // Import useNavigate from react-router-dom
import { useDispatch, useSelector } from "react-redux";
import { editUserEducation, getMasterData, getUserDetails, logoutClear, updateUserEducation } from "../../redux/actions/appActions";
import {
  SET_BOARDS,
  SET_COURSE_NAMES,
  SET_COURSE_SPECIALIZATION,
  SET_DEGREES,
  SET_UNIVERSITY,
  SET_USER_EDUCATION,
} from "../../redux/actions/actionTypes";
import { ROUTE_COMPLETE_PROFILE, ROUTE_EXPERIENCE_NEW, ROUTE_MAIN } from "../../constants/routes";
import { CssSelectTextField } from "../common/CssTextField";
import moment from "moment";
import ProfileTabs from "../common/ProfileTabs";
import { RootState } from "../../redux/types/reduxState";
import { PercentOutlined } from "@mui/icons-material";
import { allowOnlyNumbers, getKeyValuePairs } from "../../redux/services/utils";
import MultiSelect from "../common/MultiSelect";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
  backgroundColor: "rgb(255, 137, 96)",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Avatar1 = styled(Avatar)({
  width: "51px",
  height: "51px",
});

const JobType = styled("div")(() => ({
  display: "flex",
  justifyContent: "flex-start", // Align to the left
  gap: "8px",
  fontSize: "16px",
}));

const CustomButton = ({ title, link, width, disabled }: any) => {
  const navigate = useNavigate(); // useNavigate hook from react-router-dom
  const handleClick = () => {
    navigate(link); // Navigate to the specified link
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled}
      type="submit"
      sx={{
        backgroundColor: "#756DE9",
        borderRadius: "99px",
        fontSize: "16px",
        textTransform: "capitalize",
        width: width,
        "&:hover": {
          backgroundColor: "#756DE9",
        },
      }}
      variant="contained"
      size="small"
      style={{ padding: "15px 40px" }}
      className="w-100 mt-2 btn-pad"
    >
      {title}
    </Button>
  );
};

const StyledTextBox = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
  marginLeft: "10px",
  width: "100%",
});

const StyledTextBox1 = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
});

const StyledTextBox2 = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
});

const jobTypes = ["Degree", "Board"];

const styles = {
  button: {
    padding: "15px 50px",
    color: "#777777",
    backgroundColor: "#fef1ed",
    borderRadius: "99px",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "capitalize",
    width: "100%",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#ffffff",
    },
  },
};

type FormValues = Yup.InferType<any>;

const educationSchema = Yup.object().shape({
  type: Yup.string().required("Please select education type (Board or Degree)"),

  // Fields for Board education (conditionally required) — { label, value } from MultiSelect / creatable
  board: Yup.mixed()
    .nullable()
    .when("type", {
      is: "Board",
      then: (schema) =>
        schema.test("board-required", "Please enter or select board", (value: any) => {
          return !!(value && value.label && value.value);
        }),
      otherwise: (schema) => schema.nullable(),
    }),
  school: Yup.string().when("type", {
    is: "Board",
    then: (schema) => schema.required("Please enter school name"),
    otherwise: (schema) => schema.nullable(), // Optional for Degree
  }),
  completionYear: Yup.string().when("type", {
    is: "Board",
    then: (schema) => schema.required("Please select completion year"),
    otherwise: (schema) => schema.nullable(), // Optional for Degree
  }),

  // Fields for Degree education
  degree: Yup.mixed()
    .nullable()
    .test("degree-required", "Please select degree", function (value: any) {
      const type = this.parent.type;
      if (type === "Degree") {
        return value && value.label && value.value ? true : false;
      }
      return true;
    }),

  courseName: Yup.mixed()
    .nullable()
    .test("courseName-required", "Please enter course name", function (value: any) {
      const type = this.parent.type;
      if (type === "Degree") {
        return value && value.label && value.value ? true : false;
      }
      return true;
    }),
  graduate: Yup.boolean(),
  university: Yup.string().when("type", {
    is: "Degree",
    then: (schema) => schema.required("Please enter university name"),
    otherwise: (schema) => schema.nullable(),
  }),
  specialization: Yup.mixed()
    .nullable()
    .test("specialization-required", "Please enter specialization", function (value: any) {
      const type = this.parent.type;
      if (type === "Degree") {
        return value && value.label && value.value ? true : false;
      }
      return true;
    }),
  minYear: Yup.string().when("type", {
    is: "Degree",
    then: (schema) => schema.required("Please enter minimum year"),
    otherwise: (schema) => schema.nullable(),
  }),
  maxYear: Yup.string().when(["type", "graduate"], {
    is: (type: any, graduate: any) => type === "Degree" && graduate === false,
    then: (schema) => schema.required("Please enter maximum year"),
    otherwise: (schema) => schema.nullable(),
  }),

  // Combined logic for percentage and grades (handling both Board and Degree)
  percentage: Yup.string().when("type", {
    is: "Board",
    then: (schema) =>
      schema.test(
        "percentage-or-grades-or-gpa-board",
        "Either percentage or gpa must be provided for Board",
        function (value) {
          const { grades, gpa } = this.parent;
          if (!value && !grades && !gpa) {
            return false; // If all are empty, return false
          }
          return true;
        }
      ),
    otherwise: (schema) =>
      schema.when("graduate", {
        is: (graduate: any) => graduate === false,
        then: (schema) =>
          schema.test(
            "percentage-or-grades-or-gpa-degree",
            "Either percentags or gpa must be provided for Degree",
            function (value) {
              const { grades, gpa } = this.parent;
              if (!value && !grades && !gpa) {
                return false;
              }
              return true;
            }
          ),
        otherwise: (schema) => schema.nullable(),
      }),
  }),
  grades: Yup.string().when("type", {
    is: "Board",
    then: (schema) =>
      schema.test(
        "grades-or-percentage-or-gpa-board",
        "Either percentage or gpa must be provided for Board",
        function (value) {
          const { percentage, gpa } = this.parent;
          if (!value && !percentage && !gpa) {
            return false;
          }
          return true;
        }
      ),
    otherwise: (schema) =>
      schema.when("graduate", {
        is: (graduate: any) => graduate === false,
        then: (schema) =>
          schema.test(
            "grades-or-percentage-or-gpa-degree",
            "Either percentage or gpa must be provided for Degree",
            function (value) {
              const { percentage, gpa } = this.parent;
              if (!value && !percentage && !gpa) {
                return false;
              }
              return true;
            }
          ),
        otherwise: (schema) => schema.nullable(),
      }),
  }),
  // gpa: Yup.string().nullable(),
});

const schema = Yup.object().shape({
  education: Yup.array().of(educationSchema),
});

const education = {
  type: "Degree",
  board: null as any,
  school: "",
  completionYear: "",
  grades: "",
  degree: { label: "", value: "" },
  courseName: { label: "", value: "" },
  graduate: false,
  university: "",
  specialization: { label: "", value: "" },
  minYear: "",
  maxYear: "",
  percentage: "",
  educationId: "",
  gpa: "",
};

const defaultValues = {
  education: [education],
};

const startYear = moment().year() - 60; // Adjust the range as needed
const endYear = moment().year() + 10;

const EducationNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);
  const userEducation = useSelector((state: RootState) => state.app.userEducation);
  const { degrees, courseNames, courseSpecializations, boards } = useSelector((state: any) => state.app);

  const Logout = () => {
    dispatch(logoutClear(() => {
      navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const { handleSubmit, control, watch, setValue } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation,
  } = useFieldArray({
    control,
    name: "education",
  });

  useEffect(() => {
    dispatch(getMasterData("BOARD", SET_BOARDS));
    dispatch(getMasterData("DEGREE", SET_DEGREES));
    dispatch(getMasterData("UNIVERSITY", SET_UNIVERSITY));
    dispatch(getMasterData("COURSE_NAME", SET_COURSE_NAMES));
    dispatch(getMasterData("COURSE_SPECIALISATION", SET_COURSE_SPECIALIZATION));

    dispatch(getUserDetails(SET_USER_EDUCATION, "education"));
  }, []);

  useEffect(() => {
    if (userEducation?.length) {
      const data = userEducation.map((el: any) => {
        return {
          educationId: el?.educationId,
          type: el?.educationType,
          board: el?.board ? { label: el.board, value: el.board } : null,
          school: el?.schoolName,
          completionYear: el?.endDate,
          grades: el?.grades?.split(" ")[0],
          degree: { label: el?.degree, value: el?.degree },
          courseName: { label: el?.courseName, value: el?.courseName },
          graduate: el?.yetToGraduate,
          university: el?.institutionName,
          specialization: { label: el?.specialization, value: el?.specialization },
          minYear: el?.startDate,
          maxYear: el?.endDate,
          percentage: el?.grades?.split(" ")[0],
          gpa: { label: el?.gpa, value: el?.gpa },
        };
      });

      setValue("education", data);
    }
  }, [userEducation]);

  const onSubmit = (data: FormValues) => {
    const education = data?.education?.map((el: any) => {
      return {
        educationId: el?.educationId,
        year: el?.completionYear,
        grades: el?.board ? (el?.grades ? `${el?.grades} %` : "") : el?.percentage ? `${el?.percentage} %` : "",
        educationType: el?.type,
        board:
          el?.board && typeof el.board === "object"
            ? el.board.value ?? el.board.label
            : el?.board ?? "",
        schoolName: el?.school,
        degree: el?.degree?.value,
        courseName: el?.courseName?.value,
        yetToGraduate: el?.graduate,
        institutionName: el?.university,
        specialization: el?.specialization?.value,
        startDate: el?.minYear,
        endDate: el?.maxYear || el?.completionYear,
        gpa: el?.gpa?.value,
      };
    });

    const isEqual = (edu1: any, edu2: any) => {
      return edu1.educationId === edu2.educationId; // Assuming each education object has a unique `id` field
    };

    // Find missing education objects in `userEducation`
    const missingEducation = userEducation
      ?.filter((userEdu: any) => {
        return !education.some((edu: any) => isEqual(edu, userEdu));
      })
      .map((el: any) => ({
        ...el,
        deleted: true,
      }));

    // Append missing education objects to `education`
    const updatedEducation = [...education, ...missingEducation];

    if (userEducation.length) {
      dispatch(
        editUserEducation(updatedEducation, () =>
          navigate(location?.search?.includes("edit") ? `${ROUTE_COMPLETE_PROFILE}?tab=1` : ROUTE_EXPERIENCE_NEW)
        )
      );
    } else {
      dispatch(
        updateUserEducation(education, () =>
          navigate(location?.search?.includes("edit") ? `${ROUTE_COMPLETE_PROFILE}?tab=1` : ROUTE_EXPERIENCE_NEW)
        )
      );
    }
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#eeeeee" width="100%">
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <HomeIcon />
            </InputAdornment>
          </Box>
          <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <LogoutIcon />
            </InputAdornment>
          </Box>
        </Box>
      </Box>

      <ProfileTabs />
      <Box display="flex" alignItems="center" sx={{ backgroundColor: "#eee" }} padding="5px 16px">
        <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
        <StyledTextBox p={1} mt={2}>
          <Box px={1}>
            <Typography
              style={{
                fontFamily: '"DM Sans", sans-serif !important',
                fontSize: "14px !important",
                fontWeight: "medium !important",
                color: "#1F2235",
              }}
            >
              1/2
            </Typography>
            <Typography
              style={{
                fontFamily: '"DM Sans", sans-serif !important',
                fontSize: "14px !important",
                fontWeight: "medium !important",
                color: "#1F2235",
              }}
            >
              Let&apos;s talk about education!
            </Typography>
          </Box>
        </StyledTextBox>
      </Box>
      <Box display="flex" alignItems="center" p={2} sx={{ backgroundColor: "#eee" }} padding="5px 16px">
        <StyledTextBox1 p={2} py={1}>
          Please provide information on all of your degrees and boards, if applicable.
        </StyledTextBox1>
      </Box>
      <Box display="flex" alignItems="center" p={2} sx={{ backgroundColor: "#eee" }} padding="5px 14px">
        <StyledTextBox2 p={2} py={1} mb={2}>
          Lets add your latest qualification first. It’s mandatory.
        </StyledTextBox2>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {educationFields?.map((el, index) => {
          return (
            <Box key={el.id} sx={{ mb: 2 }}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="left"
                fontSize="14px"
                fontWeight="500"
                color="#757575"
              >
                <Controller
                  control={control}
                  name={`education.${index}.type`}
                  render={({ field, fieldState: { error } }) => (
                    <Box>
                      <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                        value={field.value}
                        onChange={(e) => {
                          const newType = e.target.value;
                          field.onChange(newType);

                          // Clear all fields and reinitialize when switching between Board and Degree
                          // Board fields
                          setValue(`education.${index}.board`, null);
                          setValue(`education.${index}.school`, "");
                          setValue(`education.${index}.completionYear`, "");
                          setValue(`education.${index}.grades`, "");

                          // Degree fields
                          setValue(`education.${index}.degree`, { label: "", value: "" });
                          setValue(`education.${index}.courseName`, { label: "", value: "" });
                          setValue(`education.${index}.university`, "");
                          setValue(`education.${index}.specialization`, { label: "", value: "" });
                          setValue(`education.${index}.minYear`, "");
                          setValue(`education.${index}.maxYear`, "");
                          setValue(`education.${index}.percentage`, "");
                          setValue(`education.${index}.graduate`, false);

                          // Common fields
                          setValue(`education.${index}.gpa`, { label: "", value: "" });
                        }}
                        sx={{ display: "flex", gap: 5, ml: 3 }}
                      >
                        {jobTypes.map((jobType) => {
                          return (
                            <FormControlLabel
                              key={jobType}
                              value={jobType}
                              control={<Radio />}
                              label={
                                <JobType>
                                  <Typography>{jobType}</Typography>
                                </JobType>
                              }
                            />
                          );
                        })}
                      </RadioGroup>
                      <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                        {error ? error?.message : " "}
                      </FormHelperText>
                    </Box>
                  )}
                />
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                p={2}
                bgcolor="#ffffff"
                sx={{ borderRadius: "20px 20px 0px 0px" }}
              >
                {watch(`education.${index}.type`) === "Degree" ? (
                  <>
                    <Box>
                      <Typography mt={2}>Degree</Typography>
                    </Box>
                    <MultiSelect
                      multiple={false}
                      name={`education.${index}.degree`}
                      control={control}
                      options={getKeyValuePairs(degrees)}
                      creatable={true}
                    />

                    {watch(`education.${index}.degree`)?.value && (
                      <>
                        <Box>
                          <Typography mt={2}>Course Name</Typography>
                        </Box>

                        <Controller
                          control={control}
                          name={`education.${index}.courseName`}
                          render={({ field, fieldState: { error } }) => {
                            const selectedDegree = watch(`education.${index}.degree`);

                            // Ensure selectedDegree is an object and has a "value" key
                            const degreeValue = selectedDegree?.value || "";

                            // Check if the selected degree exists in the predefined degrees list
                            const isDegreeFromList = degrees.some((degree: string) => degree === degreeValue);

                            // If degree is custom (not in list), show all courses. Otherwise, filter by degree
                            const filteredCourseNames = isDegreeFromList
                              ? courseNames
                                .filter(
                                  (course: string) =>
                                    typeof degreeValue === "string" &&
                                    course?.toLowerCase()?.startsWith(`${degreeValue.toLowerCase()}_`)
                                )
                                .map((course: string) => course.replace(`${degreeValue}_`, ""))
                              : Array.from(new Set(courseNames.map((course: string) => {
                                // For custom degrees, show all courses without prefix filtering
                                const parts = course.split("_");
                                return parts.length > 1 ? parts.slice(1).join("_") : course;
                              })));

                            return (
                              <>
                                <MultiSelect
                                  multiple={false}
                                  name={`education.${index}.courseName`}
                                  control={control}
                                  options={getKeyValuePairs(filteredCourseNames)}
                                  creatable={true}
                                  noError={true}
                                />
                                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                                  {error ? error?.message : " "}
                                </FormHelperText>
                              </>
                            );
                          }}
                        />
                      </>
                    )}

                    {watch(`education.${index}.courseName`)?.value && (
                      <>
                        <Box>
                          <Typography mt={2}>Course Specialization</Typography>
                        </Box>

                        <Controller
                          control={control}
                          name={`education.${index}.specialization`}
                          render={({ field, fieldState: { error } }) => {
                            const selectedCourse = watch(`education.${index}.courseName`);
                            const courseValue = selectedCourse?.value || "";

                            // Check if the selected course exists in the predefined courseNames list
                            const isCourseFromList = courseNames.some((course: string) =>
                              course.toLowerCase().includes(`_${courseValue.toLowerCase()}`)
                            );

                            // If course is custom (not in list), show all specializations. Otherwise, filter by course
                            const filteredSpecializations = isCourseFromList
                              ? courseSpecializations
                                .filter((specialization: string) =>
                                  specialization.toLowerCase().startsWith(`${courseValue.toLowerCase()}_`)
                                )
                                .map((specialization: string) => specialization.replace(`${courseValue}_`, ""))
                              : Array.from(new Set(courseSpecializations.map((specialization: string) => {
                                // For custom courses, show all specializations without prefix filtering
                                const parts = specialization.split("_");
                                return parts.length > 1 ? parts.slice(1).join("_") : specialization;
                              })));

                            return (
                              <>
                                <MultiSelect
                                  multiple={false}
                                  name={`education.${index}.specialization`}
                                  control={control}
                                  options={getKeyValuePairs(filteredSpecializations)}
                                  creatable={true}
                                  noError={true}
                                />
                                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                                  {error ? error?.message : " "}
                                </FormHelperText>
                              </>
                            );
                          }}
                        />
                      </>
                    )}

                    <Box>
                      <Typography mt={2}>University/Institute Name</Typography>
                    </Box>

                    <Controller
                      control={control}
                      name={`education.${index}.university`}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <TextField
                            {...field}
                            error={!!error}
                            helperText={error?.message || " "}
                            placeholder="Enter here"
                            variant="outlined"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "95px",
                                boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                "& fieldset": {
                                  border: "none",
                                },
                                "& input": {
                                  padding: "12px 24px",
                                },
                              },
                              marginTop: "10px",
                            }}
                          />
                        </>
                      )}
                    />

                    <Box>
                      <Controller
                        control={control}
                        name={`education.${index}.graduate`}
                        render={({ field }) => (
                          <>
                            <Checkbox value={field.value} onChange={field.onChange} />{" "}
                            <span>I&apos;m yet to graduate</span>
                          </>
                        )}
                      />
                    </Box>

                    <Box>
                      <Typography mt={2}>Year of Graduation</Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                      <Controller
                        control={control}
                        name={`education.${index}.minYear`}
                        render={({ field, fieldState: { error } }) => (
                          <FormControl className="social-m" sx={{ flex: 1 }}>
                            <Select
                              className="me-0 dob-btn"
                              sx={{
                                borderRadius: "99px",
                                boxShadow: "inset 0 0 11px 2px #ebe8e8",
                                "& fieldset": {
                                  border: "none",
                                },
                                "& .MuiSelect-select": {
                                  padding: "12.3px 16px", // Proper padding
                                },
                                width: "100%", // Ensure the Select component takes full width of the FormControl
                              }}
                              displayEmpty
                              value={field.value}
                              onChange={field.onChange}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return "From";
                                }
                                return selected;
                              }}
                            >
                              <MenuItem
                                sx={{
                                  whiteSpace: "normal", // Allow text to wrap
                                  wordWrap: "break-word", // Break long words to fit within the container
                                  fontSize: "0.875rem", // Adjust font size
                                  paddingY: 0,
                                }}
                                disabled
                                value=""
                              >
                                From
                              </MenuItem>
                              {Array.from({ length: endYear - startYear + 1 }).map((_, index) => {
                                const year = startYear + index;
                                return (
                                  <MenuItem
                                    sx={{
                                      whiteSpace: "normal", // Allow text to wrap
                                      wordWrap: "break-word", // Break long words to fit within the container
                                      fontSize: "0.875rem", // Adjust font size
                                      paddingY: 0,
                                    }}
                                    key={year}
                                    value={year}
                                  >
                                    {year}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                              {error ? error?.message : " "}
                            </FormHelperText>
                          </FormControl>
                        )}
                      />
                      <Controller
                        control={control}
                        name={`education.${index}.maxYear`}
                        render={({ field, fieldState: { error } }) => (
                          <FormControl className="social-m" sx={{ flex: 1 }}>
                            <Select
                              disabled={watch(`education.${index}.graduate`)}
                              className="me-0 dob-btn"
                              sx={{
                                borderRadius: "99px",
                                boxShadow: "inset 0 0 11px 2px #ebe8e8",
                                "& fieldset": {
                                  border: "none",
                                },
                                "& .MuiSelect-select": {
                                  padding: "12.3px 16px",
                                },
                                width: "100%",
                              }}
                              displayEmpty
                              value={field.value}
                              onChange={field.onChange}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return "To";
                                }
                                return selected;
                              }}
                            >
                              <MenuItem disabled value="">
                                To
                              </MenuItem>
                              {Array.from({ length: endYear - startYear + 1 }).map((_, index) => {
                                const year = startYear + index;
                                return (
                                  <MenuItem
                                    sx={{
                                      whiteSpace: "normal", // Allow text to wrap
                                      wordWrap: "break-word", // Break long words to fit within the container
                                      fontSize: "0.875rem", // Adjust font size
                                      paddingY: 0,
                                    }}
                                    key={year}
                                    value={year}
                                  >
                                    {year}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                              {error ? error?.message : " "}
                            </FormHelperText>
                          </FormControl>
                        )}
                      />
                    </Box>

                    <Typography mt={2}>Percentages</Typography>

                    <Controller
                      name={`education.${index}.percentage`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error?.message || " "}
                          placeholder="Enter here"
                          variant="outlined"
                          disabled={watch(`education.${index}.graduate`)}
                          onChange={(e) => {
                            const regex = /^[0-9\b]+$/;
                            if (e.target.value === "" || regex.test(e.target.value)) {
                              field.onChange(e.target.value.slice(0, 10));
                            }
                          }}
                          onKeyPress={(e) => allowOnlyNumbers(e)}
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <PercentOutlined style={{ color: "#777777" }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "95px",
                              boxShadow: "inset 0 0 11px 2px #dfd9d9",
                              "& fieldset": {
                                border: "none",
                              },
                              "& input": {
                                padding: "12px 24px",
                              },
                            },
                            marginTop: "10px",
                          }}
                        />
                      )}
                    />
                    <Typography mt={2}>GPA</Typography>
                    <MultiSelect
                      multiple={false}
                      name={`education.${index}.gpa`}
                      control={control}
                      disabled={watch(`education.${index}.graduate`)}
                      options={getKeyValuePairs([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])}
                    />
                  </>
                ) : (
                  <>
                    <Box>
                      <Typography mt={2}>Board</Typography>
                    </Box>

                    <MultiSelect
                      multiple={false}
                      name={`education.${index}.board`}
                      control={control}
                      options={getKeyValuePairs(boards ?? [])}
                      freesolo
                    />
                    <Box>
                      <Typography mt={2}>Name of School / college</Typography>
                    </Box>

                    <Controller
                      name={`education.${index}.school`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error?.message || " "}
                          placeholder="Enter here"
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "95px",

                              boxShadow: "inset 0 0 11px 2px #dfd9d9",
                              "& fieldset": {
                                border: "none",
                              },
                              "& input": {
                                padding: "12px 24px",
                              },
                            },
                            marginTop: "10px",
                          }}
                        />
                      )}
                    />

                    <Typography mt={2}>Completion Year</Typography>
                    <Controller
                      control={control}
                      name={`education.${index}.completionYear`}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <CssSelectTextField fullWidth error={!!error}>
                            <Select
                              sx={{
                                borderRadius: "99px",
                                boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                "& .MuiSelect-select": {
                                  padding: "12.3px 16px", // Proper padding
                                },
                                mt: 1,
                              }}
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={field?.value}
                              error={!!error}
                              placeholder="Mumbai"
                              onChange={field.onChange}
                            >
                              {Array.from({ length: endYear - startYear + 1 }).map((_, index) => {
                                const year = startYear + index;
                                return (
                                  <MenuItem
                                    sx={{
                                      whiteSpace: "normal", // Allow text to wrap
                                      wordWrap: "break-word", // Break long words to fit within the container
                                      fontSize: "0.875rem", // Adjust font size
                                      paddingY: 0,
                                    }}
                                    key={year}
                                    value={year}
                                  >
                                    {year}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </CssSelectTextField>
                          <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                            {error ? error?.message : " "}
                          </FormHelperText>
                        </>
                      )}
                    />
                    <Typography mt={2}>Percentages</Typography>

                    <Controller
                      name={`education.${index}.grades`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error?.message || " "}
                          placeholder="Enter here"
                          variant="outlined"
                          onChange={(e) => {
                            const regex = /^[0-9\b]+$/;
                            if (e.target.value === "" || regex.test(e.target.value)) {
                              field.onChange(e.target.value.slice(0, 10));
                            }
                          }}
                          onKeyPress={(e) => allowOnlyNumbers(e)}
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <PercentOutlined style={{ color: "#777777" }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "95px",

                              boxShadow: "inset 0 0 11px 2px #dfd9d9",
                              "& fieldset": {
                                border: "none",
                              },
                              "& input": {
                                padding: "12px 24px",
                              },
                            },
                            marginTop: "10px",
                          }}
                        />
                      )}
                    />

                    <Typography mt={2}>GPA</Typography>
                    <MultiSelect
                      multiple={false}
                      name={`education.${index}.gpa`}
                      control={control}
                      options={getKeyValuePairs([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])}
                    />
                  </>
                )}

                {educationFields?.length >= 1 ? (
                  <Button onClick={() => removeEducation(index)} sx={styles.button} variant="contained" size="small">
                    <img src="/images/delete-bin-line.png" alt="" width="18px" /> Delete
                  </Button>
                ) : null}
              </Box>
            </Box>
          );
        })}
        <Box mx={2} display="flex" alignItems="center" sx={{ backgroundColor: "#eee" }} px={1} pt={3} pb={3}>
          <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
          <StyledTextBox px={1}>
            <Box p={1}>
              <Typography
                style={{
                  fontFamily: '"DM Sans", sans-serif !important',
                  fontSize: "14px !important",
                  fontWeight: "medium !important",
                  color: "#1F2235",
                }}
              >
                Would you like to add another degree or board?
              </Typography>
            </Box>
          </StyledTextBox>
        </Box>
        <Box sx={{ mt: 2, textAlign: "right", mr: 2, mb: 2 }}>
          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              width: "75px",
              cursor: "pointer",
              border: "1px dashed #756DE9",
              color: "#756DE9",
              backgroundColor: "#fff !important",
            }}
            className="px-3 py-2 me-2 mb-2 roundedone"
            onClick={() => appendEducation(education)}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              width: "75px",
              cursor: "pointer",
              border: "",
              color: "",
              backgroundColor: "#756DE9",
            }}
            className="px-3 py-2 me-2 mb-2 roundedone"
          // onClick={() => setShow(false)}
          >
            No
          </Button>
        </Box>

        <Box display="flex" flexDirection="column" p={2} bgcolor="#ffffff" sx={{ borderRadius: "20px 20px 0px 0px" }}>
          {location?.search?.includes("edit") ? (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                justifyContent: "center",
                alignItems: "center",
                padding: "15px 64px",
                marginTop: "20px",
                marginBottom: "15px",
                fontSize: "16px",
                fontWeight: 600,
                color: "#ffffff",
                backgroundColor: "#756DE9",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                borderRadius: "95px",
                textTransform: "none",
              }}
            >
              Save
            </Button>
          ) : (
            <Box display="flex">
              <Button
                sx={{
                  backgroundColor: "white", // Change background color to white
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "25%",
                  color: "#000000",
                  "&:hover ": {
                    backgroundColor: "#f5f5f5", // Change hover background color if needed
                  },
                }}
                variant="contained"
                size="small"
                style={{ padding: "15px 40px" }}
                className="w-100 mt-2 btn-pad"
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <Box flexGrow={1}></Box>
              <CustomButton title="Next Up: Experience" width="70%" disabled={!!ajaxCallsInProgress} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EducationNew;
